import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import Icon from '@mdi/react';
import { mdiAlert, mdiArrowLeft, mdiMessageText, mdiRefresh, mdiSend } from '@mdi/js';
import { bookingApi } from '../api/booking';
import avatarPlaceholder from '../assets/avatar_user_5.png';
import './MessageThread.css';

const MESSAGE_CHAR_LIMIT = 500;
const COMPOSER_MAX_HEIGHT = 140; // px — composer grows to here, then scrolls internally

/**
 * MessageThread — the reusable conversation surface for one booking.
 *
 * Hosted two ways:
 *   - Desktop: the RIGHT PANE of the two-pane MessagesView (no modal).
 *   - Mobile/narrow: the body of a full-screen slide-in (pass showBackButton).
 *
 * Contains: a slim header (avatar + name + booking context + "View booking
 * details"), a scrollable bubble thread, and a composer that grows with content
 * then caps (so Send is never pushed off-screen). Booking-management controls
 * are deliberately absent — the only escape hatch is onViewBookingDetails.
 *
 * Data is booking-backed (Phase 1) via bookingApi.getMessages / sendMessage /
 * markAllMessagesAsRead — same API as the booking-detail thread, so the two
 * surfaces stay consistent.
 */
const MessageThread = ({
    booking,
    currentUserId,
    onMessageSent,
    onMessagesRead,
    onViewBookingDetails,
    onBack,            // optional — renders a back arrow (mobile full-screen)
    showBackButton = false
}) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoadingMessages, setIsLoadingMessages] = useState(false);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [messageError, setMessageError] = useState(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    // Hosts pass onMessagesRead inline (new identity each render). Read it via a ref so
    // the parent re-render that marking-read causes doesn't re-run the load effect,
    // which made the thread flash back to "Loading messages..." and refetch.
    const onMessagesReadRef = useRef(onMessagesRead);
    onMessagesReadRef.current = onMessagesRead;

    const loadMessages = useCallback(async () => {
        if (!booking?.id) return;

        try {
            setIsLoadingMessages(true);
            const { messages: fetchedMessages } = await bookingApi.getMessages(booking.id);
            setMessages(fetchedMessages);

            const hasUnread = fetchedMessages.some(
                msg => !msg.read && msg.receiverId === currentUserId
            );
            if (hasUnread) {
                await bookingApi.markAllMessagesAsRead(booking.id);
                onMessagesReadRef.current?.(booking.id);
            }
        } catch (error) {
            console.error('Error loading messages:', error);
            setMessageError('Could not load messages. Please refresh and try again.');
        } finally {
            setIsLoadingMessages(false);
        }
    }, [booking?.id, currentUserId]);

    useEffect(() => {
        setMessages([]);
        setNewMessage('');
        setMessageError(null);
        loadMessages();
    }, [booking?.id, loadMessages]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Auto-grow the composer up to COMPOSER_MAX_HEIGHT, then let it scroll.
    const autoGrow = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        const next = Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT);
        el.style.height = `${next}px`;
        // Only show the scrollbar once content exceeds the cap.
        el.style.overflowY = el.scrollHeight > COMPOSER_MAX_HEIGHT ? 'auto' : 'hidden';
    }, []);

    useEffect(() => {
        autoGrow();
    }, [newMessage, autoGrow]);

    if (!booking) return null;

    const formatMessageTime = timestamp => {
        const dt = DateTime.fromISO(timestamp);
        if (!dt.isValid) return '';
        const diff = DateTime.now().diff(dt, ['days', 'hours', 'minutes']);
        if (diff.days >= 7) return dt.toFormat('MMM d');
        if (diff.days >= 1) return `${Math.floor(diff.days)}d ago`;
        if (diff.hours >= 1) return `${Math.floor(diff.hours)}h ago`;
        if (diff.minutes >= 1) return `${Math.floor(diff.minutes)}m ago`;
        return 'Just now';
    };

    const bookingContext = () => {
        const datePart = booking.bookingDate
            ? DateTime.fromISO(booking.bookingDate).toFormat('EEE, MMM d')
            : '';
        const timePart =
            booking.timeSlot?.startTime && booking.timeSlot?.endTime
                ? `${booking.timeSlot.startTime} - ${booking.timeSlot.endTime}`
                : '';
        return [datePart, timePart].filter(Boolean).join(', ');
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || isSendingMessage) return;

        try {
            setIsSendingMessage(true);
            setMessageError(null);
            await bookingApi.sendMessage(booking.id, newMessage.trim());
            setNewMessage('');
            await loadMessages();
            if (onMessageSent) onMessageSent(booking.id);
        } catch (error) {
            console.error('Error sending message:', error);
            setMessageError('Failed to send message. Please check your connection and try again.');
        } finally {
            setIsSendingMessage(false);
        }
    };

    return (
        <div className="message-thread">
            {/* Header — avatar, name, slim booking context, escape hatch */}
            <div className="message-thread-header">
                {showBackButton && (
                    <button className="message-thread-back" onClick={onBack} aria-label="Back to conversations">
                        <Icon path={mdiArrowLeft} size={1} />
                    </button>
                )}
                <img
                    className="message-thread-avatar"
                    src={booking.studentInfo?.profileImageUrl || avatarPlaceholder}
                    alt={booking.studentName || 'Student avatar'}
                    onError={e => { e.target.onerror = null; e.target.src = avatarPlaceholder; }}
                />
                <div className="message-thread-heading">
                    <div className="message-thread-name">{booking.studentName || 'Conversation'}</div>
                    <div className="message-thread-context">{bookingContext()}</div>
                </div>
                {onViewBookingDetails && (
                    <button
                        className="message-thread-view-booking"
                        onClick={() => onViewBookingDetails(booking)}
                    >
                        View booking details
                    </button>
                )}
            </div>

            {/* Thread */}
            <div className="message-thread-body">
                {isLoadingMessages ? (
                    <div className="message-thread-loading">Loading messages...</div>
                ) : messages.length > 0 ? (
                    <>
                        {messages.map(msg => {
                            const isOwnMessage = msg.senderId === currentUserId;
                            return (
                                <div
                                    key={msg.id}
                                    className={`message-thread-bubble ${isOwnMessage ? 'is-sent' : 'is-received'}`}
                                >
                                    {!isOwnMessage && (
                                        <div className="message-thread-sender">{msg.senderName}</div>
                                    )}
                                    <div className="message-thread-text">{msg.message}</div>
                                    <div className="message-thread-time">{formatMessageTime(msg.sentAt)}</div>
                                </div>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </>
                ) : (
                    <div className="message-thread-empty">
                        <Icon path={mdiMessageText} size={1.5} color="var(--dl-color-gray-400)" />
                        <p>No messages yet</p>
                    </div>
                )}
            </div>

            {/* Composer */}
            <div className="message-thread-composer">
                {messageError && (
                    <div className="message-thread-error">
                        <Icon path={mdiAlert} size={0.7} />
                        {messageError}
                    </div>
                )}
                <div className="message-thread-composer-row">
                    <textarea
                        ref={textareaRef}
                        className="message-thread-input"
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={e => {
                            setNewMessage(e.target.value);
                            if (messageError) setMessageError(null);
                        }}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                            }
                        }}
                        maxLength={MESSAGE_CHAR_LIMIT}
                        rows={1}
                    />
                    <button
                        className="message-thread-send-btn"
                        onClick={handleSendMessage}
                        disabled={!newMessage.trim() || isSendingMessage}
                        aria-label="Send message"
                    >
                        <Icon path={isSendingMessage ? mdiRefresh : mdiSend} size={0.85} spin={isSendingMessage ? 1 : 0} />
                    </button>
                </div>
                <div className="message-thread-char-count">
                    {newMessage.length}/{MESSAGE_CHAR_LIMIT}
                </div>
            </div>
        </div>
    );
};

export default MessageThread;
